/**
 * Philips Hue: API-Vertrag (Schemas + DTOs) und Farbumrechnung für Szenen-Vorschauen.
 */
import { z } from "zod";

// ---------------------------------------------------------------------------
// Eingaben
// ---------------------------------------------------------------------------

/** Ressourcen-IDs der CLIP-API v2 sind UUIDs. */
export const hueResourceIdSchema = z
  .string()
  .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)
  .transform((value) => value.toLowerCase());

/** Bridge-ID wie in /api/0/config („001788FFFE285DA9“), intern klein geschrieben. */
export const hueBridgeIdSchema = z
  .string()
  .trim()
  .regex(/^[0-9a-f]{16}$/i, "Ungültige Bridge-ID")
  .transform((value) => value.toLowerCase());

export const ipv4Schema = z
  .string()
  .trim()
  .regex(
    /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/,
    "Bitte eine IP-Adresse wie 192.168.1.20",
  );

export const HUE_PIN_TYPES = ["scene", "light"] as const;
export const huePinSchema = z.object({ type: z.enum(HUE_PIN_TYPES), id: hueResourceIdSchema });
export type HuePin = z.infer<typeof huePinSchema>;

export const HUE_MAX_PINS = 24;
export const putHuePinsSchema = z.object({ pins: z.array(huePinSchema).max(HUE_MAX_PINS) });

export const hueProbeSchema = z.object({ ip: ipv4Schema });
export const hueConnectSchema = z.object({ bridgeId: hueBridgeIdSchema, ip: ipv4Schema });

export const hueLevelSchema = z
  .object({
    on: z.boolean().optional(),
    brightness: z.number().min(1).max(100).optional(),
  })
  .refine((value) => value.on !== undefined || value.brightness !== undefined, "on oder brightness fehlt");
export type HueLevel = z.infer<typeof hueLevelSchema>;

// ---------------------------------------------------------------------------
// Antworten
// ---------------------------------------------------------------------------

/** Gefundene Bridge im Netzwerk. `paired`: in der App schon gekoppelt (Verbinden ohne Knopf). */
export interface HueBridgeDto {
  id: string;
  name: string;
  ip: string;
  paired: boolean;
}

export type HueProbeDto =
  { ok: true; bridge: HueBridgeDto } | { ok: false; reason: "unreachable" | "not-hue"; serverIp?: string };

export interface HueStatusDto {
  connected: boolean;
  bridge: { id: string; name: string; ip: string; reachable: boolean } | null;
  pins: HuePin[];
}

export type HueConnectDto = { status: "waiting" } | { status: "connected"; hue: HueStatusDto };

export interface HueGroupDto {
  id: string;
  kind: "room" | "zone";
  name: string;
  archetype: string;
  groupedLightId: string | null;
  on: boolean;
  /** 0–100, null wenn keine dimmbare Lampe. */
  brightness: number | null;
}

export interface HueSceneDto {
  id: string;
  name: string;
  groupId: string;
  /** 1–5 sRGB-Farben (#RRGGBB) für den Verlauf der Kachel. */
  colors: string[];
  active: boolean;
}

export interface HueLightDto {
  id: string;
  name: string;
  archetype: string;
  roomId: string | null;
  on: boolean;
  brightness: number | null;
  /** Aktuelle Lichtfarbe als sRGB, null wenn unbekannt. */
  color: string | null;
}

/** Katalog (alle Räume/Szenen/Lampen) bzw. Dock (nur angeheftete, in Pin-Reihenfolge). */
export interface HueCatalogDto {
  groups: HueGroupDto[];
  scenes: HueSceneDto[];
  lights: HueLightDto[];
}

// ---------------------------------------------------------------------------
// Farben
// ---------------------------------------------------------------------------

const OFF_COLOR = "#2A2A31";
const WARM_WHITE = "#FFD9A8";

function hex(r: number, g: number, b: number): string {
  const part = (v: number) =>
    Math.round(Math.min(1, Math.max(0, v)) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${part(r)}${part(g)}${part(b)}`.toUpperCase();
}

function gamma(v: number): number {
  return v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
}

/** Abdunkeln nach Helligkeit – nur sanft, damit gedimmte Szenen nicht schwarz wirken. */
function shade(brightness: number | undefined): number {
  if (brightness === undefined) return 1;
  return 0.55 + (0.45 * Math.min(100, Math.max(0, brightness))) / 100;
}

/** CIE-1931-xy (Hue „Wide Gamut“) → sRGB-Hex. */
export function xyToHex(x: number, y: number, brightness?: number): string {
  if (!(y > 0)) return WARM_WHITE;
  const Y = 1;
  const X = (Y / y) * x;
  const Z = (Y / y) * (1 - x - y);
  let r = X * 1.656492 - Y * 0.354851 - Z * 0.255038;
  let g = -X * 0.707196 + Y * 1.655397 + Z * 0.036152;
  let b = X * 0.051713 - Y * 0.121364 + Z * 1.01153;
  const max = Math.max(r, g, b);
  if (max > 1) [r, g, b] = [r / max, g / max, b / max];
  const k = shade(brightness);
  return hex(gamma(Math.max(0, r)) * k, gamma(Math.max(0, g)) * k, gamma(Math.max(0, b)) * k);
}

/** Farbtemperatur in Mirek (153–500) → sRGB-Hex (Näherung nach Tanner Helland). */
export function mirekToHex(mirek: number, brightness?: number): string {
  const t = 1_000_000 / Math.min(1000, Math.max(100, mirek)) / 100;
  const r = t <= 66 ? 255 : 329.698727446 * Math.pow(t - 60, -0.1332047592);
  const g =
    t <= 66 ? 99.4708025861 * Math.log(t) - 161.1195681661 : 288.1221695283 * Math.pow(t - 60, -0.0755148492);
  const b = t >= 66 ? 255 : t <= 19 ? 0 : 138.5177312231 * Math.log(t - 10) - 305.0447927307;
  const k = shade(brightness);
  return hex((r / 255) * k, (g / 255) * k, (b / 255) * k);
}

interface ColorLike {
  on?: { on?: boolean };
  dimming?: { brightness?: number };
  color?: { xy?: { x: number; y: number } };
  color_temperature?: { mirek?: number | null };
}

/** Farbe einer Lampe bzw. Szenen-Aktion; null, wenn ausgeschaltet bzw. ohne Farbinfo. */
export function colorOf(state: ColorLike): string | null {
  if (state.on?.on === false) return null;
  const brightness = state.dimming?.brightness;
  if (state.color?.xy) return xyToHex(state.color.xy.x, state.color.xy.y, brightness);
  if (state.color_temperature?.mirek) return mirekToHex(state.color_temperature.mirek, brightness);
  return state.on?.on || brightness !== undefined ? mirekToHex(370, brightness) : null;
}

export interface SceneColorSource {
  palette?: {
    color?: { color: { xy: { x: number; y: number } }; dimming?: { brightness?: number } }[];
    color_temperature?: { color_temperature: { mirek: number }; dimming?: { brightness?: number } }[];
  };
  actions?: { action: ColorLike }[];
}

/** 1–5 Farben für die Szenen-Kachel: Palette, sonst die Lampen-Aktionen der Szene. */
export function sceneColors(scene: SceneColorSource, max = 5): string[] {
  const colors: string[] = [];
  for (const entry of scene.palette?.color ?? []) {
    colors.push(xyToHex(entry.color.xy.x, entry.color.xy.y, entry.dimming?.brightness));
  }
  for (const entry of scene.palette?.color_temperature ?? []) {
    colors.push(mirekToHex(entry.color_temperature.mirek, entry.dimming?.brightness));
  }
  if (colors.length === 0) {
    for (const { action } of scene.actions ?? []) {
      const color = colorOf(action);
      if (color) colors.push(color);
    }
  }
  const unique = [...new Set(colors)].slice(0, max);
  if (unique.length > 0) return unique;
  return (scene.actions ?? []).length > 0 ? [OFF_COLOR] : [WARM_WHITE];
}

/** CSS-Verlauf für eine Szenen-Kachel. */
export function sceneGradient(colors: string[]): string {
  if (colors.length === 0) return WARM_WHITE;
  if (colors.length === 1) return colors[0]!;
  return `linear-gradient(135deg, ${colors.join(", ")})`;
}
